export class DateParser {
    private static dateFormats = [
        // ISO format
        /^\d{4}-\d{2}-\d{2}$/,
        // dd/MM/yyyy
        /^(\d{2})\/(\d{2})\/(\d{4})$/,
        // MM/dd/yyyy
        /^(\d{2})\/(\d{2})\/(\d{4})$/,
        // yyyy/MM/dd
        /^(\d{4})\/(\d{2})\/(\d{2})$/,
        // dd-MM-yyyy
        /^(\d{2})-(\d{2})-(\d{4})$/,
        // MM-dd-yyyy
        /^(\d{2})-(\d{2})-(\d{4})$/,
        // dd.MM.yyyy
        /^(\d{2})\.(\d{2})\.(\d{4})$/,
        // yyyy.MM.dd
        /^(\d{4})\.(\d{2})\.(\d{2})$/
    ]

    private static isValidDateValues(
        year: number,
        month: number,
        day: number
    ): boolean {
        const date = new Date(year, month - 1, day)

        return (
            date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day
        )
    }

    private static extractDateParts(
        dateString: string
    ): { year: number; month: number; day: number } | null {
        // Try ISO format first (yyyy-MM-dd)
        let match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/)

        if (match) {
            return {
                year: parseInt(match[1]),
                month: parseInt(match[2]),
                day: parseInt(match[3])
            }
        }

        // Try dd/MM/yyyy or MM/dd/yyyy
        match = dateString.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
        if (match) {
            return {
                year: parseInt(match[3]),
                month: parseInt(match[2]),
                day: parseInt(match[1])
            }
        }

        // Try yyyy/MM/dd
        match = dateString.match(/^(\d{4})\/(\d{2})\/(\d{2})$/)
        if (match) {
            return {
                year: parseInt(match[1]),
                month: parseInt(match[2]),
                day: parseInt(match[3])
            }
        }

        // Try dd-MM-yyyy or MM-dd-yyyy
        match = dateString.match(/^(\d{2})-(\d{2})-(\d{4})$/)
        if (match) {
            return {
                year: parseInt(match[3]),
                month: parseInt(match[2]),
                day: parseInt(match[1])
            }
        }

        // Try dd.MM.yyyy
        match = dateString.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
        if (match) {
            return {
                year: parseInt(match[3]),
                month: parseInt(match[2]),
                day: parseInt(match[1])
            }
        }

        return null
    }

    static parseDate(dateString: string | null | undefined): Date | null {
        if (!dateString) {
            return null
        }

        const normalized = dateString.trim()

        try {
            // Primero intentamos identificar manualmente el formato para evitar
            // que el constructor de Date interprete de forma errónea (p. ej. MM/dd vs dd/MM)
            const parts = this.extractDateParts(normalized)

            if (
                parts &&
                this.isValidDateValues(parts.year, parts.month, parts.day)
            ) {
                return new Date(parts.year, parts.month - 1, parts.day)
            }

            // Como respaldo, usar el constructor nativo (ISO, strings con zona horaria, etc.)
            const fallbackDate = new Date(normalized)

            if (!isNaN(fallbackDate.getTime())) {
                return fallbackDate
            }
        } catch (e) {
            // If any parsing fails, return null
        }

        return null
    }

    static formatDate(date: Date | null | undefined): string {
        if (!date || isNaN(date.getTime())) {
            return ''
        }

        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')

        return `${year}-${month}-${day}`
    }

    static isValidDate(date: any): boolean {
        if (!date) {
            return false
        }

        if (date instanceof Date) {
            return !isNaN(date.getTime())
        }

        const parsedDate = this.parseDate(String(date))

        return parsedDate !== null
    }
}
